import type { ReactNode } from 'react';
import { cn } from './cn.js';

/**
 * Panel — the surface every block of content sits on.
 *
 * Depth comes from a hairline border plus a short two-layer shadow, not from a
 * big soft blur. `flush` exists because tables and lists need to reach the
 * panel edge while prose needs padding, and that distinction was the main
 * thing making the old cards look boxy.
 */
export interface PanelProps {
  title?: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
  footer?: ReactNode;
  /** Removes body padding, for tables and full-bleed lists. */
  flush?: boolean;
  className?: string;
  bodyClassName?: string;
  children?: ReactNode;
}

export function Panel({
  title,
  subtitle,
  action,
  footer,
  flush,
  className,
  bodyClassName,
  children,
}: PanelProps) {
  return (
    <section
      className={cn(
        'card-soft relative flex min-w-0 flex-col overflow-hidden rounded-lg',
        className,
      )}
    >
      {(title || action) && (
        <header className="flex items-start justify-between gap-3 px-5 pb-2 pt-4">
          <div className="min-w-0">
            {title && (
              <h2 className="text-[0.9375rem] font-semibold leading-tight text-ink">{title}</h2>
            )}
            {subtitle && (
              <p className="mt-1 text-xs leading-snug text-ink-secondary">{subtitle}</p>
            )}
          </div>
          {action && <div className="shrink-0">{action}</div>}
        </header>
      )}

      <div className={cn('min-w-0 flex-1', !flush && 'px-5 pb-5 pt-2', bodyClassName)}>
        {children}
      </div>

      {footer && (
        <footer className="border-t border-line-subtle bg-surface-sunken/50 px-5 py-3">
          {footer}
        </footer>
      )}
    </section>
  );
}

/**
 * A single metric. The label sits above a large tabular number, with the
 * supporting figure beneath — so a row of these scans as one line of numbers.
 */
export function Metric({
  label,
  value,
  hint,
  tone = 'default',
  icon,
  trend,
  className,
}: {
  label: ReactNode;
  value: ReactNode;
  hint?: ReactNode;
  tone?: 'default' | 'ok' | 'warn' | 'danger' | 'brand';
  icon?: ReactNode;
  /** A sparkline or other small visual, sat beside the number. */
  trend?: ReactNode;
  className?: string;
}) {
  const toneClass = {
    default: 'text-ink',
    ok: 'text-ok',
    warn: 'text-warn',
    danger: 'text-danger',
    brand: 'text-brand',
  }[tone];

  return (
    <div
      className={cn(
        'card-soft card-lift group relative overflow-hidden rounded-lg p-4',
        className,
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-2xs font-medium uppercase tracking-[0.08em] text-ink-tertiary">
          {label}
        </span>
        {icon && (
          <span aria-hidden className="text-ink-tertiary [&>svg]:h-3.5 [&>svg]:w-3.5">
            {icon}
          </span>
        )}
      </div>
      <div className="mt-1.5 flex items-end justify-between gap-3">
        <span className={cn('display nums text-[1.9rem] leading-none', toneClass)}>{value}</span>
        {trend && <span className="shrink-0 pb-0.5">{trend}</span>}
      </div>
      {hint && <p className="mt-2 text-2xs leading-snug text-ink-tertiary">{hint}</p>}
    </div>
  );
}

/** Page header: title, description and actions on one baseline. */
export function PageHeader({
  title,
  description,
  actions,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <header className={cn('mb-5 flex flex-wrap items-end justify-between gap-3', className)}>
      <div className="min-w-0">
        <h1 className="display text-[1.6rem] leading-tight text-ink">{title}</h1>
        {description && (
          <p className="mt-1 text-xs leading-relaxed text-ink-secondary">{description}</p>
        )}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </header>
  );
}
